/**
 * Custom hook for managing user account tier
 * Provides real-time account tier information and refresh functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { subscriptionService, AccountTier } from '../services/subscriptionService';

export const useAccountTier = (userId: string | undefined) => {
  const [accountTier, setAccountTier] = useState<AccountTier>('PAYG');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccountTier = useCallback(async () => {
    if (!userId) {
      setAccountTier('PAYG');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Ensure user has a subscription (creates default if needed)
      await subscriptionService.ensureUserHasSubscription(userId);
      
      const tier = await subscriptionService.getUserAccountTier(userId);
      setAccountTier(tier || 'PAYG');
    } catch (err) {
      console.error('Error fetching account tier:', err);
      setError('Failed to fetch account tier');
      setAccountTier('PAYG');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Fetch account tier on mount and when userId changes
  useEffect(() => {
    fetchAccountTier();
  }, [fetchAccountTier]);

  // Function to refresh account tier (useful after payments)
  const refreshAccountTier = useCallback(() => {
    fetchAccountTier();
  }, [fetchAccountTier]);

  return {
    accountTier,
    isLoading,
    error,
    refreshAccountTier
  };
};